/* eslint-disable @typescript-eslint/no-explicit-any */
// Design v11: after a refresh or a desktop switch you land where you were — the horizontal scroll, each column's
// vertical scroll and the open task (also the ones parked in other columns) are restored per desktop (reference
// snapView / saveView / restoreView / paneScroller). The reference stores the view on its desktop object; production
// keeps it device-local in localStorage, keyed by workspace, user and desktop id, so it is never synced with the
// personal layout (ViewSync) and another device keeps its own scroll positions.
import type { Pane } from '@/domain/model';
import { deskSnapshot } from './v10desks';

type C = any;
export interface DeskView { x: number; y: Record<string, number>; sel: { paneId: string; taskId: string } | null; bg: Record<string, { paneId: string; taskId: string }>; picked: string | null }

const KEY = 'taskpanes-v3';
const viewKey = (c: C, deskId: string) => KEY + '-view:' + (c.repo.workspaceId || 'demo') + ':' + c.state.me + ':' + deskId;

export function loadView(c: C, deskId: string): DeskView | null {
  try { const v = JSON.parse(c.prefs.get(viewKey(c, deskId)) || 'null'); return v && typeof v === 'object' ? v as DeskView : null; } catch { return null; }
}
function storeView(c: C, deskId: string, v: DeskView): void { try { c.prefs.set(viewKey(c, deskId), JSON.stringify(v)); } catch { /* storage unavailable */ } }

/** The element that scrolls a column vertically (reference paneScroller). */
export function paneScroller(el: Element): HTMLElement | null {
  let best: HTMLElement | null = null, bh = 0;
  [el, ...el.querySelectorAll(':scope > *, :scope > * > *')].forEach(n => { const oy = getComputedStyle(n).overflowY; const h = (n as HTMLElement).clientHeight; if ((oy === 'auto' || oy === 'scroll') && h > bh) { best = n as HTMLElement; bh = h; } });
  return best;
}

export function snapView(c: C): DeskView | null {
  const root = c.rootRef && c.rootRef.current as HTMLElement | null; if (!root) return null; const y: Record<string, number> = {};
  root.querySelectorAll('[data-pane-id]').forEach((el: Element) => { const sc = paneScroller(el); if (sc) y[el.getAttribute('data-pane-id')!] = sc.scrollTop; });
  const sel = c.state.sel && c.state.sel.taskId ? { paneId: c.state.sel.paneId, taskId: c.state.sel.taskId } : null;
  const ids = new Set((c.state.panes as Pane[]).map(p => p.id)), bg: DeskView['bg'] = {};
  Object.entries(c.state.bgSel || {}).forEach(([k, v]: [string, any]) => { if (v && v.taskId && ids.has(k)) bg[k] = { paneId: v.paneId || k, taskId: v.taskId }; });
  return { x: root.scrollLeft, y, sel, bg, picked: c.state.picked || null };
}

const selKeyOf = (v: DeskView) => JSON.stringify([v.sel, v.bg, v.picked]);

/** Saves the current desktop's view (skipped while a restore is still settling). */
export function saveView(c: C, deskId?: string): void {
  if (c._restoring) return; const v = snapView(c); if (!v) return;
  c._lastSelKey = selKeyOf(v);
  storeView(c, deskId || deskSnapshot(c).cur, v);
}

/** Restores a saved view: open tasks first, then the scroll positions a few times while the columns lay out. */
export function restoreView(c: C, v: DeskView | null): void {
  if (!v) return; c._restoring = true;
  const has = (p: { paneId: string; taskId: string }) => (c.state.panes as Pane[]).some(x => x.id === p.paneId) && !!c.findTask(p.taskId);
  const bg: DeskView['bg'] = {}; Object.entries(v.bg || {}).forEach(([k, x]) => { if (x && has(x) && !(v.sel && v.sel.paneId === k)) bg[k] = x; });
  const sl = v.sel && has(v.sel) ? v.sel : null;
  if (sl || Object.keys(bg).length) { if (sl) c._noReveal = true; c.setState({ sel: sl, picked: sl ? (v.picked || sl.taskId) : c.state.picked, bgSel: bg, cardUI: {} }); }
  const go = () => { const root = c.rootRef && c.rootRef.current as HTMLElement | null; if (!root) return; root.scrollLeft = v.x || 0;
    root.querySelectorAll('[data-pane-id]').forEach((el: Element) => { const t = (v.y || {})[el.getAttribute('data-pane-id')!]; if (t == null) return; const sc = paneScroller(el); if (sc) sc.scrollTop = t; }); };
  clearTimeout(c._rvT); (c._rvTs || []).forEach((x: number) => clearTimeout(x));
  c._rvTs = [0, 80, 250, 600, 1100].map(ms => setTimeout(go, ms));
  c._rvT = setTimeout(() => { go(); c._restoring = false; const v2 = snapView(c); if (v2) c._lastSelKey = selKeyOf(v2); }, 1300);
}

/** Listeners: save shortly after scrolling, when leaving the page or hiding it; restore the current desktop at start. */
export function viewListeners(c: C): () => void {
  const cur = deskSnapshot(c).cur, v0 = loadView(c, cur);
  if (v0) { c._restoring = true; setTimeout(() => restoreView(c, v0), 0); }
  const svw = () => { if (c._restoring) return; clearTimeout(c._svT); c._svT = setTimeout(() => saveView(c), 250); };
  const unload = () => saveView(c), vis = () => { if (document.hidden) saveView(c); };
  document.addEventListener('scroll', svw, true); window.addEventListener('beforeunload', unload); document.addEventListener('visibilitychange', vis);
  return () => { document.removeEventListener('scroll', svw, true); window.removeEventListener('beforeunload', unload); document.removeEventListener('visibilitychange', vis); clearTimeout(c._svT); clearTimeout(c._rvT); };
}

/** componentDidUpdate: a change of the open / parked / highlighted task saves the view (reference _lastSelKey). */
export function viewDidUpdate(c: C): void {
  if (c._restoring) return;
  const bg: DeskView['bg'] = {}; Object.entries(c.state.bgSel || {}).forEach(([k, v]: [string, any]) => { if (v && v.taskId) bg[k] = { paneId: v.paneId || k, taskId: v.taskId }; });
  const sl = c.state.sel && c.state.sel.taskId ? { paneId: c.state.sel.paneId, taskId: c.state.sel.taskId } : null;
  const key = JSON.stringify([sl, bg, c.state.picked || null]);
  if (key !== c._lastSelKey) { c._lastSelKey = key; clearTimeout(c._svT); c._svT = setTimeout(() => saveView(c), 120); }
}
